import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { CommonCodeColumn, WarehouseColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultWMS2020_R01Dto, WMS2020_R01Dto } from './Dto_WMS2020_R01';
import { useWMS2020_R01Store } from './Store_WMS2020_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS2020_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<WMS2020_R01Dto> | null>;
}

export const WMS2020_R01Table = (props: Props) => {
	const { clickable, setClickable, gridRef } = props;

	const {
		WMS2020_R01List,
		setWMS2020_R01List,
		updateWMS2020_R01List,
		selectedWMS2020_R01List,
		setSelectedWMS2020_R01List,
		initSelectedWMS2020_R01List,
		allSelectedWMS2020_R01List,
		originalWMS2020_R01List,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useWMS2020_R01Store();

	const { wmsCdList, warehouseCdList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS2020_R01List,
		defaultGridData: defaultWMS2020_R01Dto,
		selectedRow: selectedWMS2020_R01List,
		originalGridData: originalWMS2020_R01List,
		updateRow: (index: number, data: WMS2020_R01Dto) => updateWMS2020_R01List(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		logicCenter: '물류센터',
		warehouseCd: '창고코드',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		itemStatus: '재고등급',
		itemNo: '품목번호',
		lotNo: 'LOT No',
		lotQty: '전산재고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'logicCenter')(params), []);

	const baseColumnDefs: ColDef<WMS2020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 100,
			...CommonCodeColumn.options(wmsCdList, false, undefined, undefined),
			cellStyle: (p) => getCellStyle(p, 'logicCenter'),
		},
		{
			headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 100,
			...WarehouseColumn.options(warehouseCdList, false, (params: any) => params.data?.logicCenter, undefined, undefined),
			cellStyle: (p) => getCellStyle(p, 'warehouseCd'),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 140,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '재고등급', colId: 'itemStatus', field: 'itemStatus', width: 100,
			cellStyle: (p) => getCellStyle(p, 'itemStatus'),
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 140,
			cellStyle: (p) => getCellStyle(p, 'modelNo'),
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 140,
			cellStyle: (p) => getCellStyle(p, 'lotNo'),
		},
		{
			headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'lotQty'),
		},
	], [headerNames, wmsCdList, warehouseCdList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS2020_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS2020_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'valueIndex' as const,
		selectedIds: selectedWMS2020_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedWMS2020_R01List(ids as any),
		isRowSelectable: (event: RowClickedEvent<WMS2020_R01Dto>) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			return !!event.data;
		},
	}), [selectedWMS2020_R01List, setSelectedWMS2020_R01List, clickable, setClickable]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		logicCenter: `${WMS2020_R01List.length.toLocaleString()}건`,
	}]), [WMS2020_R01List.length]);

	return (
		<GridContextWrapper<WMS2020_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setWMS2020_R01List}
			initSelected={initSelectedWMS2020_R01List}
			allSelected={allSelectedWMS2020_R01List}
			defaultRowData={defaultWMS2020_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={[{
				label: '모든 행 선택',
				onClick: () => {
					allSelectedWMS2020_R01List();
				},
			}]}
		>
			<Grid_Touch<WMS2020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<WMS2020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedWMS2020_R01List,
					deletedList: [],
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
